import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Camera, Upload, X, Check, Plus, AlertCircle, Sparkles, Building2, Globe, Instagram, Youtube, Twitter, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/lib/api';
import { PaymentSetupButton } from '@/components/CardSetupWidget';

const PRESET_NICHES = [
  'Beauty', 'Fashion', 'Fitness', 'Gaming', 'Tech', 'Food',
  'Travel', 'Lifestyle', 'Music', 'Comedy', 'Education', 'Business',
  'Art', 'Sports', 'Parenting', 'Finance', 'Wellness', 'Automotive',
];

const TEAM_SIZES = ['1-5', '6-20', '21-100', '100+'];
const GOVERNING_LAWS = [
  { code: 'US-CA', label: 'California, USA' },
  { code: 'US-NY', label: 'New York, USA' },
  { code: 'GB-EW', label: 'England & Wales' },
  { code: 'AU-NSW', label: 'New South Wales, Australia' },
  { code: 'CA-ON', label: 'Ontario, Canada' },
  { code: 'EU-DE', label: 'Germany' },
  { code: 'EU-FR', label: 'France' },
];

function isMobile() {
  if (typeof navigator === 'undefined') return false;
  return /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
}

// ── Avatar uploader: device-aware (mobile = camera, desktop = drop zone) ──
function AvatarUploader({ value, onChange, label = 'Profile picture' }) {
  const fileRef = useRef(null);
  const cameraRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const mobile = isMobile();

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB'); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await API.post('/uploads/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.url);
      toast.success('Picture uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    upload(e.dataTransfer.files?.[0]);
  };

  const fullUrl = value
    ? (value.startsWith('http') ? value : `${process.env.REACT_APP_BACKEND_URL}${value}`)
    : '';

  return (
    <div>
      <Label className="text-xs text-slate-600 mb-2 block">{label}</Label>
      <div className="flex items-center gap-4">
        <div className="relative w-20 h-20 rounded-full overflow-hidden bg-slate-100 border-2 border-slate-200 flex items-center justify-center shrink-0">
          {fullUrl ? (
            <img src={fullUrl} alt="avatar" className="w-full h-full object-cover" />
          ) : (
            <Camera className="w-7 h-7 text-slate-400" />
          )}
          {uploading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        {mobile ? (
          <div className="flex-1 grid grid-cols-2 gap-2">
            <input ref={cameraRef} type="file" accept="image/*" capture="user"
              className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="avatar-camera-input" />
            <input ref={fileRef} type="file" accept="image/*"
              className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="avatar-file-input" />
            <Button type="button" variant="outline" size="sm" onClick={() => cameraRef.current?.click()}
              data-testid="avatar-camera-btn"
              className="h-10 text-xs">
              <Camera className="w-4 h-4 mr-1" /> Take photo
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}
              data-testid="avatar-upload-btn"
              className="h-10 text-xs">
              <Upload className="w-4 h-4 mr-1" /> Choose
            </Button>
          </div>
        ) : (
          <div
            onClick={() => fileRef.current?.click()}
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            className={`flex-1 cursor-pointer rounded-xl border-2 border-dashed px-4 py-3 text-center transition-colors ${dragOver ? 'border-blue-500 bg-blue-50' : 'border-slate-300 hover:border-blue-400 bg-slate-50'}`}
            data-testid="avatar-dropzone"
          >
            <input ref={fileRef} type="file" accept="image/*"
              className="hidden" onChange={e => upload(e.target.files?.[0])} data-testid="avatar-file-input" />
            <Upload className="w-4 h-4 inline mr-1.5 text-slate-500" />
            <span className="text-xs text-slate-600">
              {value ? 'Click or drop to replace' : 'Click or drop image — JPG, PNG, WebP up to 5MB'}
            </span>
          </div>
        )}
      </div>
      {value && (
        <button type="button" onClick={() => onChange('')}
          data-testid="avatar-remove-btn"
          className="text-[11px] text-red-500 hover:underline mt-2 inline-flex items-center gap-1">
          <X className="w-3 h-3" /> Remove picture
        </button>
      )}
    </div>
  );
}

// ── Niche picker: preset chips + free-text custom add ──
function NichePicker({ value = [], onChange }) {
  const [custom, setCustom] = useState('');
  const toggle = (n) => {
    if (value.includes(n)) onChange(value.filter(x => x !== n));
    else onChange([...value, n]);
  };
  const addCustom = () => {
    const v = custom.trim();
    if (!v) return;
    if (!value.includes(v)) onChange([...value, v]);
    setCustom('');
  };
  return (
    <div>
      <Label className="text-xs text-slate-600 mb-2 block">Your niches <span className="text-slate-400">(pick all that fit)</span></Label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {PRESET_NICHES.map(n => {
          const sel = value.includes(n);
          return (
            <button key={n} type="button" onClick={() => toggle(n)}
              data-testid={`niche-${n.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${sel ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}>
              {sel && <Check className="w-3 h-3 inline mr-1" />}{n}
            </button>
          );
        })}
      </div>
      {/* custom */}
      <div className="flex gap-2 mb-2">
        <Input value={custom} onChange={e => setCustom(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustom())}
          placeholder="Add your own niche…" className="h-9 text-sm flex-1" data-testid="niche-custom-input" />
        <Button type="button" size="sm" variant="outline" onClick={addCustom}
          data-testid="niche-custom-add" className="h-9">
          <Plus className="w-3 h-3 mr-1" /> Add
        </Button>
      </div>
      {/* selected custom (not in presets) */}
      {value.filter(v => !PRESET_NICHES.includes(v)).length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.filter(v => !PRESET_NICHES.includes(v)).map(n => (
            <span key={n} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-purple-100 text-purple-700 text-xs">
              {n}
              <button type="button" onClick={() => toggle(n)} className="hover:text-purple-900"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Creator complete-profile form ──
function CreatorComplete({ onDone, onSkip }) {
  const [form, setForm] = useState({
    avatar_url: '',
    niches: [],
    social_handles: { instagram: '', tiktok: '', youtube: '', twitter: '' },
    bio: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const cleanedHandles = Object.fromEntries(
        Object.entries(form.social_handles).filter(([, v]) => (v || '').trim())
      );
      await API.post('/onboarding/creator/complete', {
        avatar_url: form.avatar_url || undefined,
        niches: form.niches.length ? form.niches : undefined,
        social_handles: Object.keys(cleanedHandles).length ? cleanedHandles : undefined,
        bio: form.bio.trim() || undefined,
      });
      toast.success('Profile complete!');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="creator-complete-form">
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">
          <strong>Heads up:</strong> Agencies can't send you proposals until your profile is complete. You can keep exploring the platform meanwhile.
        </p>
      </div>

      <AvatarUploader value={form.avatar_url} onChange={v => setForm(f => ({ ...f, avatar_url: v }))} />

      <NichePicker value={form.niches} onChange={v => setForm(f => ({ ...f, niches: v }))} />

      <div>
        <Label className="text-xs text-slate-600 mb-2 block">Social handles <span className="text-slate-400">(any you'd like agencies to see)</span></Label>
        <div className="space-y-2">
          {[
            { key: 'instagram', icon: Instagram, ph: '@yourhandle', label: 'Instagram' },
            { key: 'tiktok', icon: Sparkles, ph: '@yourhandle', label: 'TikTok' },
            { key: 'youtube', icon: Youtube, ph: 'channel name or URL', label: 'YouTube' },
            { key: 'twitter', icon: Twitter, ph: '@yourhandle', label: 'X / Twitter' },
          ].map(s => (
            <div key={s.key} className="flex items-center gap-2">
              <s.icon className="w-4 h-4 text-slate-400 shrink-0" />
              <Input
                placeholder={`${s.label} — ${s.ph}`}
                value={form.social_handles[s.key]}
                onChange={e => setForm(f => ({ ...f, social_handles: { ...f.social_handles, [s.key]: e.target.value } }))}
                className="h-9 text-sm"
                data-testid={`social-${s.key}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-xs text-slate-600 mb-2 block">Short bio <span className="text-slate-400">(optional)</span></Label>
        <Textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))}
          placeholder="A line or two about you and what you make…"
          rows={3} className="text-sm" data-testid="creator-bio" />
      </div>

      <div className="flex items-center justify-between pt-2">
        <button type="button" onClick={onSkip} className="text-xs text-slate-500 hover:text-slate-700"
          data-testid="complete-skip-btn">Skip for now</button>
        <Button onClick={submit} disabled={saving}
          className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="complete-save-btn">
          {saving ? 'Saving…' : 'Save & Continue'}
        </Button>
      </div>
    </div>
  );
}

// ── Agency complete-profile form ──
function AgencyComplete({ onDone, onSkip }) {
  const [form, setForm] = useState({
    logo_url: '',
    team_size: '',
    website: '',
    default_governing_law: '',
    default_dispute_resolution: 'arbitration',
  });
  const [saving, setSaving] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  // Pre-fill website from the agency doc (set during signup)
  useEffect(() => {
    if (prefilled) return;
    API.get('/buyer/agency-profile').then(r => {
      const agency = r.data?.agency;
      if (agency?.website) setForm(f => ({ ...f, website: agency.website }));
      setPrefilled(true);
    }).catch(() => setPrefilled(true));
  }, [prefilled]);

  const submit = async () => {
    if (!form.default_governing_law) {
      toast.error('Please select a governing law — it is required to send proposals.');
      return;
    }
    setSaving(true);
    try {
      await API.post('/onboarding/agency/complete', {
        logo_url: form.logo_url || undefined,
        team_size: form.team_size || undefined,
        website: form.website.trim() || undefined,
        default_governing_law: form.default_governing_law || undefined,
        default_dispute_resolution: form.default_dispute_resolution || undefined,
      });
      toast.success('Agency profile complete!');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="agency-complete-form">
      <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">
          <strong>Required:</strong> You must set a governing law before you can send proposals to creators. Complete this now or come back to Settings later.
        </p>
      </div>

      <AvatarUploader value={form.logo_url} onChange={v => setForm(f => ({ ...f, logo_url: v }))} label="Agency logo" />

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs text-slate-600 mb-2 block">Team size</Label>
          <Select value={form.team_size} onValueChange={v => setForm(f => ({ ...f, team_size: v }))}>
            <SelectTrigger className="h-10" data-testid="team-size-trigger"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent className="bg-white">
              {TEAM_SIZES.map(t => <SelectItem key={t} value={t}>{t} people</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-slate-600 mb-2 block">Website {form.website && <span className="text-emerald-500 text-[10px] ml-1">pre-filled from signup</span>}</Label>
          <div className="relative">
            <Globe className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
              placeholder="https://acme.com" className="h-10 pl-9" data-testid="agency-website" />
          </div>
        </div>
      </div>

      <div className="p-4 rounded-xl border-2 border-purple-200 bg-purple-50/30">
        <Label className="text-xs text-slate-700 mb-2 block font-semibold">Default governing law <span className="text-red-400">*</span></Label>
        <Select value={form.default_governing_law} onValueChange={v => setForm(f => ({ ...f, default_governing_law: v }))}>
          <SelectTrigger className="h-10 bg-white" data-testid="governing-law-trigger"><SelectValue placeholder="Select jurisdiction..." /></SelectTrigger>
          <SelectContent className="bg-white">
            {GOVERNING_LAWS.map(l => <SelectItem key={l.code} value={l.code}>{l.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-purple-600 mt-1.5">This determines which legal jurisdiction applies to your proposals. You cannot send proposals without it.</p>
      </div>

      <div>
        <Label className="text-xs text-slate-600 mb-2 block">Default dispute resolution</Label>
        <Select value={form.default_dispute_resolution} onValueChange={v => setForm(f => ({ ...f, default_dispute_resolution: v }))}>
          <SelectTrigger className="h-10" data-testid="dispute-trigger"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="arbitration">Binding arbitration</SelectItem>
            <SelectItem value="court">Courts of governing jurisdiction</SelectItem>
            <SelectItem value="mediation">Mediation first, then arbitration</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="p-4 rounded-xl border-2 border-slate-200 bg-slate-50/50">
        <div className="flex items-center gap-2 mb-3">
          <CreditCard className="w-4 h-4 text-slate-600" />
          <Label className="text-xs text-slate-700 font-semibold">Payment method</Label>
        </div>
        <p className="text-[11px] text-slate-500 mb-3">Add a payment method to fund deals with creators. Your account will not be charged now — Stripe will securely save your preferred payment method (card, direct debit, etc.) for future use.</p>
        <PaymentSetupButton buttonLabel="Set up payment method via Stripe" className="w-full border-slate-200" />
      </div>

      <div className="flex items-center justify-between pt-2">
        <button type="button" onClick={onSkip} className="text-xs text-slate-500 hover:text-slate-700"
          data-testid="complete-skip-btn">Skip for now</button>
        <Button onClick={submit} disabled={saving}
          className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="complete-save-btn">
          {saving ? 'Saving...' : 'Save & Continue'}
        </Button>
      </div>
    </div>
  );
}

// ── Outer page wrapper ──
export default function CompleteProfile() {
  const navigate = useNavigate();
  const { user, loading, refreshUser } = useAuth();

  useEffect(() => {
    // Wait until AuthContext has finished its initial hydration before deciding
    // to bounce — otherwise a freshly-verified user gets kicked back to /login
    // during the brief mount-before-state window.
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);

  if (loading || !user) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="complete-profile-loading">
      <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
  const isCreator = user.role === 'creator';
  const isAgency = user.role === 'buyer' || user.role === 'agency_admin';

  const onDone = async () => {
    await refreshUser();
    navigate(isCreator ? '/creator' : '/agency');
  };
  const onSkip = () => navigate(isCreator ? '/creator' : '/agency');

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10" data-testid="complete-profile-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl">
        <div className="text-center mb-7">
          <div className={`w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-3 ${isCreator ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
            {isCreator ? <Sparkles className="w-7 h-7" /> : <Building2 className="w-7 h-7" />}
          </div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900">
            {isCreator ? "Let's complete your creator profile" : "Set up your agency"}
          </h1>
          <p className="text-slate-500 text-sm mt-1.5">
            {isCreator
              ? "A few more details so agencies can find and reach you."
              : "Add your branding and defaults so you can start sending proposals."}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-8">
          {isCreator && <CreatorComplete onDone={onDone} onSkip={onSkip} />}
          {isAgency && <AgencyComplete onDone={onDone} onSkip={onSkip} />}
          {!isCreator && !isAgency && (
            <p className="text-sm text-slate-500">Nothing to complete for your account type.</p>
          )}
        </div>
      </motion.div>
    </div>
  );
}
